export interface InviewOptions {
  name: string;
  rootMargin: string;
  threshold: number | number[];
  timeout: number;
  once: boolean;
  props: string[];
}

export class Inview {
  props: InviewOptions;
  node: HTMLElement;
  timeout: ReturnType<typeof setTimeout> | undefined;
  observer?: IntersectionObserver | null;

  constructor(node: HTMLElement, options: Partial<InviewOptions> = {}) {
    this.props = {
      name: 'inview',
      rootMargin: '0px 0px 0px 0px',
      threshold: 1.0,
      timeout: 0,
      once: false,
      props: [],
      ...options,
    };
    this.node = node;

    /* prevent double decoration */
    if (this.node.decorator) {
      if (this.node.decorator instanceof Inview) {
        this.node.decorator.destroy();
      }
    }

    this.node.decorator = this;
    this.timeout = undefined;

    this.node.classList.add(`${this.props.name}-enter`);
    this.registerObserver();
  }

  destroy = () => {
    if (this.observer) {
      this.observer.unobserve(this.node);
    }

    this.removeClasses();
    this.observer = null;
    this.node.decorator = null;
  };

  registerObserver = () => {
    const { rootMargin, threshold } = this.props;
    this.observer = new IntersectionObserver(this.onIntersection, {
      rootMargin,
      threshold,
    });
    this.observer.observe(this.node);
  };

  removeClasses = (e?: TransitionEvent) => {
    const { props, once } = this.props;
    if (e) {
      if (props.length > 0) {
        if (props.indexOf(e.propertyName) !== -1) {
          clearTimeout(this.timeout);
          this.node.classList.remove(`${this.props.name}-enter`);
          this.node.classList.remove(`${this.props.name}-enter-active`);
        }
      } else {
        clearTimeout(this.timeout);
        this.node.classList.remove(`${this.props.name}-enter`);
        this.node.classList.remove(`${this.props.name}-enter-active`);
      }
    } else {
      clearTimeout(this.timeout);
      this.node.classList.remove(`${this.props.name}-enter`);
      this.node.classList.remove(`${this.props.name}-enter-active`);
    }

    if (once && this.observer) {
      this.observer.unobserve(this.node);
    }
  };

  onIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach(({ intersectionRatio }) => {
      if (intersectionRatio > 0) {
        this.timeout = setTimeout(this.removeClasses, this.props.timeout);
        this.node.addEventListener('transitionend', this.removeClasses);
        this.node.classList.add(`${this.props.name}-enter-active`);
      }
    });
  };
}

export default (node: HTMLElement, props?: Partial<InviewOptions>) =>
  new Inview(node, props);
